import React from "react";
import Panel from "../Panel";
import PhoneScannerScreen from "../PhoneScannerScreen";
import { stripSerial, buzzSuccess, buzzError } from "../../utils/scan";
import { codesInUse, WAREHOUSE } from "./consumableHelpers";

const STEPS = ["Choose", "Scan", "Assign"];

const labelStyle = { fontSize: 12, fontWeight: 600, color: "var(--text-primary)" };

// Panel 8b: set up tracked units in runs.
//
// A tracked type needs a QR code per unit, so units are created in a run rather than
// one at a time: choose the type, the holding location and how many, then scan that
// many labels off the roll. Labels come off in any order, so a code is matched by
// what was scanned rather than by position, and a code already in use is refused so a
// mis-scan cannot overwrite an existing unit.
export default function SetupRun({ types, locations, onAssign, consumableStock, disabled, onDone }) {
  const [step, setStep] = React.useState("setup");
  const [runType, setRunType] = React.useState("");
  const [runLoc, setRunLoc] = React.useState(WAREHOUSE);
  const [runQty, setRunQty] = React.useState(10);
  const [codes, setCodes] = React.useState([]);
  const [codeBuf, setCodeBuf] = React.useState("");
  const [codeErr, setCodeErr] = React.useState("");
  const [assigned, setAssigned] = React.useState([]);
  const [scannerOpen, setScannerOpen] = React.useState(false);
  const inputRef = React.useRef(null);

  const inUse = React.useMemo(() => codesInUse(consumableStock), [consumableStock]);
  const selectedType = types.find(t => t.id === runType) || null;
  const canStart = !!selectedType && runQty > 0 && !disabled;

  // Keep the field focused through the run so a hardware scanner, which types the
  // code and presses Enter, lands in the right place without the user clicking.
  React.useEffect(() => {
    if (step === "scan" && inputRef.current) inputRef.current.focus();
  }, [step, codes.length]);

  const commitCode = React.useCallback(
    raw => {
      const code = stripSerial(String(raw || "").trim());
      if (!code) return;
      if (inUse.has(code)) {
        setCodeErr("That code is already in use: " + code);
        buzzError();
        return;
      }
      // Checked against the codes we already hold rather than from inside the state
      // updater: React defers the updater, so a flag set in there is still false by
      // the time the lines below read it — a re-scanned label was silently dropped
      // while the scanner got a success buzz and no message.
      if (codes.includes(code)) {
        setCodeErr("Already scanned in this run: " + code);
        buzzError();
        return;
      }
      setCodes(prev => (prev.includes(code) ? prev : [...prev, code]));
      setCodeErr("");
      setCodeBuf("");
      buzzSuccess();
    },
    [inUse, codes]
  );

  const reset = React.useCallback(() => {
    setStep("setup");
    setCodes([]);
    setCodeBuf("");
    setCodeErr("");
    setScannerOpen(false);
  }, []);

  const finish = () => {
    if (!codes.length || !selectedType) return;
    onAssign({ type: selectedType, locationName: runLoc, codes });
    setAssigned(codes);
    setStep("done");
    setScannerOpen(false);
  };

  const scanned = codes.length;
  const stepIdx = step === "setup" ? 0 : step === "scan" ? 1 : 2;
  const locLabel = runLoc === WAREHOUSE ? "the warehouse" : runLoc;

  return (
    <Panel>
      {/* Step indicator */}
      <div style={{ display: "flex", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
        {STEPS.map((label, i) => (
          <span
            key={label}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              fontWeight: i === stepIdx ? 600 : 400,
              color: i === stepIdx ? "var(--text-primary)" : "var(--text-muted)",
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: 9999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 600,
                background:
                  i < stepIdx
                    ? "var(--success-fill)"
                    : i === stepIdx
                    ? "var(--brand-default)"
                    : "var(--surface-inset)",
                color:
                  i < stepIdx
                    ? "var(--success-text)"
                    : i === stepIdx
                    ? "#fff"
                    : "var(--text-muted)",
              }}
            >
              {i + 1}
            </span>
            {label}
          </span>
        ))}
      </div>

      {/* ── Step 1: choose ── */}
      {step === "setup" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={labelStyle}>Type</span>
            {types.length === 0 ? (
              <span style={{ fontSize: 12, color: "var(--danger-text)" }}>
                No type is tracked individually yet. Turn tracking on for a type in{" "}
                <strong>Settings &rsaquo; Consumables</strong> first.
              </span>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {types.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setRunType(t.id)}
                    className="chip"
                    style={
                      runType === t.id
                        ? {
                            background: "var(--brand-tint)",
                            borderColor: "var(--brand-border)",
                            color: "var(--brand-default)",
                            fontWeight: 600,
                          }
                        : undefined
                    }
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <div style={{ width: 150, display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={labelStyle}>How many</span>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: 34,
                  border: "1px solid var(--border-default)",
                  borderRadius: "var(--radius-md, 6px)",
                  overflow: "hidden",
                }}
              >
                <button
                  type="button"
                  aria-label="Decrease quantity"
                  onClick={() => setRunQty(q => Math.max(1, q - 1))}
                  style={qtyBtn}
                >
                  &minus;
                </button>
                <span
                  style={{
                    flex: 1,
                    textAlign: "center",
                    fontVariantNumeric: "tabular-nums",
                    fontSize: 15,
                    fontWeight: 600,
                    color: "var(--text-primary)",
                  }}
                >
                  {runQty}
                </span>
                <button
                  type="button"
                  aria-label="Increase quantity"
                  onClick={() => setRunQty(q => Math.min(200, q + 1))}
                  style={qtyBtn}
                >
                  +
                </button>
              </div>
            </div>

            <div style={{ flex: 1, minWidth: 180, display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={labelStyle}>Held at</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {[WAREHOUSE, ...locations.filter(l => l !== WAREHOUSE)].map(l => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setRunLoc(l)}
                    className="chip"
                    style={
                      runLoc === l
                        ? {
                            background: "var(--brand-tint)",
                            borderColor: "var(--brand-border)",
                            color: "var(--brand-default)",
                            fontWeight: 600,
                          }
                        : undefined
                    }
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, lineHeight: 1.5, color: "var(--text-muted)", flex: 1 }}>
              {selectedType
                ? `${runQty} × ${selectedType.name} at ${locLabel}. You will scan ${runQty} labels.`
                : "Choose a type to set up units."}
            </span>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!canStart}
              onClick={() => {
                if (!canStart) return;
                setCodes([]);
                setCodeErr("");
                setCodeBuf("");
                setStep("scan");
              }}
            >
              Start scanning
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: scan ── */}
      {step === "scan" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", flex: 1 }}>
              {selectedType ? selectedType.name : ""} &middot; {locLabel}
            </span>
            <span
              style={{
                fontVariantNumeric: "tabular-nums",
                fontSize: 14,
                fontWeight: 600,
                color: "var(--text-primary)",
              }}
            >
              {scanned} / {runQty}
            </span>
          </div>

          <div className="progress-wrap">
            <div
              className="progress-fill"
              style={{ width: Math.min(100, Math.round((scanned / Math.max(1, runQty)) * 100)) + "%" }}
            />
          </div>

          <div className="search-box" style={{ borderColor: "var(--brand-default)" }}>
            <input
              ref={inputRef}
              value={codeBuf}
              onChange={e => setCodeBuf(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitCode(codeBuf);
                }
              }}
              placeholder="Scan the next label, or type a code and press Enter"
              aria-label="Scan or enter a QR code"
            />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setScannerOpen(v => !v)}
            >
              {scannerOpen ? "Close camera" : "Use camera"}
            </button>
          </div>

          {scannerOpen && (
            <div style={{ borderRadius: "var(--radius-xl)", overflow: "hidden" }}>
              <PhoneScannerScreen compact onScan={commitCode} />
            </div>
          )}

          {codeErr && (
            <div
              role="alert"
              style={{
                padding: "10px 12px",
                borderRadius: "var(--radius-md, 6px)",
                background: "var(--danger-fill)",
                border: "1px solid var(--danger-border)",
                fontSize: 12,
                color: "var(--danger-text)",
              }}
            >
              {codeErr}
            </div>
          )}

          <span style={{ fontSize: 12, lineHeight: 1.5, color: "var(--text-muted)" }}>
            Labels come off the roll in any order. A code already in use is refused, so a
            mis-scan cannot overwrite an existing unit.
          </span>

          <div style={{ maxHeight: 212, overflow: "auto" }}>
            {codes.length === 0 ? (
              <div style={{ padding: 16, fontSize: 13, color: "var(--text-muted)" }}>
                Nothing scanned yet.
              </div>
            ) : (
              codes.map((c, i) => (
                <div
                  key={c}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    height: 38,
                    borderBottom: "1px solid var(--border-subtle)",
                  }}
                >
                  <span
                    style={{
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 12,
                      color: "var(--text-muted)",
                      width: 26,
                    }}
                  >
                    {i + 1}
                  </span>
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 13,
                      color: "var(--text-primary)",
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {c}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setCodes(prev => prev.filter(x => x !== c))}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={reset}>
              Cancel
            </button>
            <span style={{ flex: 1, fontSize: 12, color: "var(--text-muted)" }}>
              {scanned >= runQty
                ? "All " + runQty + " labels scanned."
                : runQty - scanned + " of " + runQty + " still to scan."}
            </span>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!scanned}
              onClick={finish}
            >
              {scanned >= runQty ? "Assign " + scanned + " units" : "Assign " + scanned + " now"}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: done ── */}
      {step === "done" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div
            style={{
              display: "flex",
              gap: 10,
              padding: 12,
              borderRadius: "var(--radius-md, 6px)",
              background: "var(--success-fill)",
              border: "1px solid var(--success-border)",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--success-text)" }}>
                {assigned.length} × {selectedType ? selectedType.name : ""} assigned
              </div>
              <div
                style={{
                  fontSize: 12,
                  lineHeight: 1.5,
                  color: "var(--success-text)",
                  marginTop: 3,
                }}
              >
                Held at {locLabel}. Each unit now moves on its own code — scanning one at
                another site moves it there.
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={labelStyle}>Assigned in this run</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {assigned.map(c => (
                <span key={c} className="chip" style={{ fontFamily: "var(--font-mono)" }}>
                  {c}
                </span>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={reset}>
              Set up more units
            </button>
            {onDone && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={onDone}>
                Back to stock
              </button>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
}

const qtyBtn = {
  width: 34,
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  color: "var(--text-muted)",
  background: "none",
  border: "none",
  font: "inherit",
  fontSize: 16,
};
